import React, { useState, useEffect, FormEvent } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box
} from '@mui/material';
import { DataService } from 'src/services/data-service';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import GenericInput from 'src/components/input-components/GenericTextInput';
import ImagePicker from 'src/components/input-components/GenericImagePicker';
import NewsForm from '../../components/news-form-component/newsFormWidget';

interface NewsItem {
  title: string;
  content: string;
  imageUrl: string;
  status: string;
}

export const EditNewItem: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<NewsItem>({
    title: '',
    content: '',
    imageUrl: '',
    status: ''
  });

  const [imagePickerOpen, setImagePickerOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const fetchNewsItem = async () => {
      if (!id) return;
      try {
        const response = await DataService.get(`api/article/get?id=${id}`);
        if (!response.ok) {
          toast.error('Failed to load news data');
          return;
        }
        const data: NewsItem = await response.json();
        setFormData({
          title: data.title || '',
          content: data.content || '',
          imageUrl: data.imageUrl || '',
          status: data.status || ''
        });
      } catch (error) {
        toast.error('Error fetching news data');
      } finally {
        setLoading(false);
      }
    };

    fetchNewsItem();
  }, [id]);

  const handleChange = (name: string, value: string) => {
    console.log(`Changing ${name}:`, value);
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        Title: formData.title,
        Content: formData.content,
        ImageUrl: formData.imageUrl
      };

      const response = await DataService.post(
        `api/article/update/${id}`,
        payload
      );
      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(errorMessage);
      }

      toast.success('News article updated successfully!');
      navigate(`/web-builder/news`);
    } catch (err) {
      console.error('Error updating news:', err);
      toast.error('Failed to update news article.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Edit News
          </Typography>

          {/* Title Input */}
          <GenericInput
            type="text"
            name="title"
            title="Title"
            value={formData.title}
            onChange={(value) => handleChange('title', value)}
          />

          {/* Image Preview */}
          {formData.imageUrl && (
            <img
              src={formData.imageUrl}
              alt="preview"
              style={{
                width: '20%',
                maxHeight: '500px',
                marginBottom: '10px'
              }}
            />
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">Select an Image</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setImagePickerOpen(true)}
            >
              Choose Image
            </Button>
          </Box>

          {/* Image Picker Dialog */}
          <ImagePicker
            open={imagePickerOpen}
            onClose={() => setImagePickerOpen(false)}
            onSelect={(imageUrl) => {
              handleChange('imageUrl', imageUrl as string);
              setImagePickerOpen(false);
            }}
          />

          {/* Content Editor */}
          <NewsForm
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
          />

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ mt: 2 }}
            disabled={submitting}
          >
            {submitting ? 'Updating...' : 'Update News'}
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};
